import { useEffect, useState } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { DeadlinesConfig } from '@vedjegy/shared';

// Convert ISO datetime string (UTC) → datetime-local input value (local time)
function toDateTimeInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert datetime-local input value → ISO string for API
function toISO(local: string): string | null {
  return local ? new Date(local).toISOString() : null;
}

// Format a datetime-local value (YYYY-MM-DDTHH:MM) in Hungarian ISO 8601 style: YYYY. MM. DD. HH:MM
function formatHunDateTime(val: string): string {
  if (!val) return '';
  const [datePart, timePart] = val.split('T');
  if (!datePart) return '';
  const [y, m, d] = datePart.split('-');
  return timePart ? `${y}. ${m}. ${d}. ${timePart}` : `${y}. ${m}. ${d}.`;
}

// Format a date input value (YYYY-MM-DD) in Hungarian ISO 8601 style: YYYY. MM. DD.
function formatHunDate(val: string): string {
  if (!val) return '';
  const [y, m, d] = val.split('-');
  return `${y}. ${m}. ${d}.`;
}

type FormState = {
  applicationStart: string;
  applicationEnd:   string;
  reviewStart:      string;
  reviewEnd:        string;
  minMemberSince:   string;
};

export default function AdminDeadlinesPage() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  const [form, setForm] = useState<FormState>({
    applicationStart: '',
    applicationEnd:   '',
    reviewStart:      '',
    reviewEnd:        '',
    minMemberSince:   '',
  });

  useEffect(() => {
    api.config.getDeadlines()
      .then((d: DeadlinesConfig) => setForm({
        applicationStart: toDateTimeInput(d.applicationStart),
        applicationEnd:   toDateTimeInput(d.applicationEnd),
        reviewStart:      toDateTimeInput(d.reviewStart),
        reviewEnd:        toDateTimeInput(d.reviewEnd),
        // minMemberSince is stored as a plain DATE — use as-is (YYYY-MM-DD)
        minMemberSince:   d.minMemberSince ? d.minMemberSince.slice(0, 10) : '',
      }))
      .catch(() => setError('Nem sikerült betölteni a határidőket.'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k: keyof FormState, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function handleSave() {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.config.updateDeadlines({
        applicationStart: toISO(form.applicationStart),
        applicationEnd:   toISO(form.applicationEnd),
        reviewStart:      toISO(form.reviewStart),
        reviewEnd:        toISO(form.reviewEnd),
        minMemberSince:   form.minMemberSince || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Mentés sikertelen.');
    } finally {
      setSaving(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 700,
    color: 'var(--secondary-foreground)', marginBottom: 4,
  };
  const datePreviewStyle: React.CSSProperties = {
    marginTop: 4, fontSize: 11, color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', fontSize: 14,
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: '#fff', color: 'var(--primary)',
  };
  const sectionHeadStyle: React.CSSProperties = {
    margin: '0 0 14px', fontSize: 13, fontWeight: 700,
    color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '0.04em',
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px', maxWidth: 680 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
          Határidők
        </h1>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: 'var(--muted-foreground)' }}>
          A beállított dátumok határolják, mikor lehet pályázatot beadni, illetve bírálni.
          Üres mező esetén nincs korlátozás az adott irányban.
        </p>

        {loading ? (
          <p style={{ color: 'var(--muted-foreground)' }}>Betöltés...</p>
        ) : (
          <div style={{
            background: '#fff', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '28px 32px',
            boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 24,
          }}>
            {/* Application window */}
            <div>
              <p style={sectionHeadStyle}>Pályázatbeadási időszak</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Nyitás</label>
                  <input
                    style={inputStyle}
                    type="datetime-local"
                    value={form.applicationStart}
                    onChange={(e) => set('applicationStart', e.target.value)}
                  />
                  {form.applicationStart && (
                    <div style={datePreviewStyle}>{formatHunDateTime(form.applicationStart)}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Zárás</label>
                  <input
                    style={inputStyle}
                    type="datetime-local"
                    value={form.applicationEnd}
                    onChange={(e) => set('applicationEnd', e.target.value)}
                  />
                  {form.applicationEnd && (
                    <div style={datePreviewStyle}>{formatHunDateTime(form.applicationEnd)}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Review window */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <p style={sectionHeadStyle}>Bírálati időszak</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Nyitás</label>
                  <input
                    style={inputStyle}
                    type="datetime-local"
                    value={form.reviewStart}
                    onChange={(e) => set('reviewStart', e.target.value)}
                  />
                  {form.reviewStart && (
                    <div style={datePreviewStyle}>{formatHunDateTime(form.reviewStart)}</div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Zárás</label>
                  <input
                    style={inputStyle}
                    type="datetime-local"
                    value={form.reviewEnd}
                    onChange={(e) => set('reviewEnd', e.target.value)}
                  />
                  {form.reviewEnd && (
                    <div style={datePreviewStyle}>{formatHunDateTime(form.reviewEnd)}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Membership date threshold */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <p style={sectionHeadStyle}>Tagság jogosultsági feltétele</p>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--muted-foreground)' }}>
                Csak azok kezdhetnek előminősítést, akiknek a kamarai belépési dátuma ennél a napnál korábbi.
                Üres mező esetén nincs dátumfeltétel.
              </p>
              <div style={{ maxWidth: 240 }}>
                <label style={labelStyle}>Legkésőbbi belépési dátum</label>
                <input
                  style={inputStyle}
                  type="date"
                  value={form.minMemberSince}
                  onChange={(e) => set('minMemberSince', e.target.value)}
                />
                {form.minMemberSince && (
                  <div style={datePreviewStyle}>{formatHunDate(form.minMemberSince)}</div>
                )}
              </div>
            </div>

            {error && (
              <div style={{ fontSize: 13, color: '#c0392b' }}>{error}</div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                className="btn btn-primary"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Mentés...' : 'Mentés'}
              </button>
              {saved && (
                <span style={{ fontSize: 13, color: '#1a7a40' }}>✓ Mentve</span>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
